import React from "react";
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
} from "reactstrap";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import { connect } from "react-redux";
import loadingAction from "../../core/redux/actions/loadingAction";
import ReactTable from "react-table";
import TablePagination from "components/Table/TablePagination";
import strings from "../../core/translate";
import SelectRow from "../../components/Select/SelectRow";
import { Scopes } from "../../shared/scopes.enum";
import { getGarageBrowseSections } from "../../shared/garage-sections";
import PageHeader from "../../components/Page/PageHeader";
import RowActions, {
  editAction,
  toggleActiveAction,
} from "components/Table/RowActions";
import GarageFormModal from "./GarageFormModal";

class MerchantBranchList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      branches: [],
      merchants: [],
      selectedMerchant: "",
      search: "",
      view: "cards",
      // The closed garages are their own list rather than greyed-out rows in
      // this one: they are not part of the day's work, and mixing them in
      // makes every count and every search result wrong.
      showDeactivated: false,
      formOpen: false,
      editing: null,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const merchantIdFromStorage = sessionStorage.getItem("merchantId");
    const { selectedMerchant } = this.props.match.params;

    if (merchantIdFromStorage) {
      this.setState({ selectedMerchant: merchantIdFromStorage }, this.callPage);
      this.fetchMerchants();
    } else if (selectedMerchant && selectedMerchant !== ":selectedMerchant") {
      this.setState({ selectedMerchant }, this.callPage);
      this.fetchMerchants();
    } else {
      this.fetchMerchants();
    }
  }

  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      const merchants = response.data.items.map((merchant) => ({
        value: merchant.id,
        label: merchant.name,
      }));
      this.setState({ isLoading: false, merchants }, () => {
        // a single merchant account never has to pick one
        if (merchants.length === 1 && !this.state.selectedMerchant) {
          this.setState({ selectedMerchant: merchants[0].value }, this.callPage);
        }
      });
    }
  };

  callPage = async () => {
    const { selectedMerchant } = this.state;
    if (!selectedMerchant || selectedMerchant === ":selectedMerchant") {
      this.setState({ isLoading: false });
      this.props.loadingAction(false);
      return;
    }

    this.setState({ isLoading: true });
    // The api lists the open garages unless asked otherwise, so every other
    // screen keeps offering only garages that can still take work.
    const response = await this.engine.getItem(
      `merchants/${selectedMerchant}/branches?isActive=${this.state.showDeactivated ? "false" : "true"}`
    );
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        branches: response.data.items.map((item) => ({
          data: item,
          id: item.id,
          name: item.name || item.address,
          address: item.address,
          area: item.area ? item.area.name : "",
          city: item.area && item.area.city ? item.area.city.name : "",
          vehicleMakeList:
            item.vehicleMake instanceof Array
              ? item.vehicleMake.map((make) => make.name).join(", ")
              : item.vehicleMake || "",
          phoneNumber: item.phoneNumber || "",
          isActive: item.isActive !== false,
          created: new Date(item.createdAt),
        })),
      });
    } else {
      this.setState({ isLoading: false });
    }
    this.props.loadingAction(false);
  };

  handleMerchantSelect = (event) => {
    const selectedMerchant = event.value;
    this.setState({ selectedMerchant, branches: [] }, () => {
      this.props.history.push(`/admin/merchant/${selectedMerchant}/branches`);
      this.callPage();
    });
  };

  // add and edit both happen in one dialog, no page change
  handleAdd = () => {
    const { selectedMerchant } = this.state;
    if (!selectedMerchant || selectedMerchant === ":selectedMerchant") {
      alert(strings.select + " " + strings.merchant);
      return;
    }
    this.setState({ formOpen: true, editing: null });
  };

  handleEdit = (branch) => {
    this.setState({ formOpen: true, editing: branch.data });
  };

  handleCloseForm = () => this.setState({ formOpen: false, editing: null });

  handleSaved = () => {
    this.setState({ formOpen: false, editing: null }, this.callPage);
  };

  /**
   * Opens or closes a garage. There is no delete: a garage owns its workers,
   * job cards, bookings and work orders, and removing it strands all of them —
   * the staff accounts in particular stay live, attached to a garage nobody
   * can reach. Closing it takes it out of the app and out of every picker,
   * keeps its records, and can be undone.
   */
  handleToggleActive = async (branch) => {
    const isActive = !branch.isActive;
    const confirmText = isActive
      ? strings.confirm_activate_garage
      : strings.confirm_deactivate_garage;
    if (!window.confirm(strings.formatString(confirmText, branch.name))) return;

    this.props.loadingAction(true);
    const { selectedMerchant } = this.state;
    const result = await this.engine.patchItem(
      `merchants/${selectedMerchant}/branches/${branch.id}/active`,
      { isActive }
    );
    if (result) {
      this.callPage();
    }
    this.props.loadingAction(false);
  };

  // The two sides are separate lists, so switching one empties the other
  // rather than leaving rows from the wrong side on screen.
  handleToggleView = () =>
    this.setState(
      (prev) => ({ showDeactivated: !prev.showDeactivated, branches: [] }),
      this.callPage
    );

  // opens one of the garage sections (services, photos, ratings, ...)
  handleOpenSection = (branch, section) => {
    this.props.history.push({
      pathname: section.path,
      state: {
        selectedMerchant: this.state.selectedMerchant,
        branchName: branch.name,
      },
    });
  };

  getFilteredBranches() {
    const { branches, search } = this.state;
    const term = search.trim().toLowerCase();
    if (!term) return branches;

    return branches.filter((branch) =>
      [
        branch.name,
        branch.address,
        branch.city,
        branch.area,
        branch.phoneNumber,
        branch.vehicleMakeList,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }

  // edit and the open/closed switch sit on the card, not behind a hidden menu
  renderGarageMenu(branch, canWrite) {
    if (!canWrite) return null;
    return (
      <div className="garage-card__menu">
        <RowActions
          actions={[
            editAction(() => this.handleEdit(branch)),
            toggleActiveAction(branch.isActive, () =>
              this.handleToggleActive(branch)
            ),
          ]}
        />
      </div>
    );
  }

  renderCards(branches, canWrite) {
    return (
      <Row>
        {branches.map((branch) => {
          const sections = getGarageBrowseSections(branch.id);
          return (
            <Col lg="4" md="6" sm="12" key={branch.id}>
              <Card
                className={`garage-card${
                  branch.isActive ? "" : " is-deactivated"
                }`}
              >
                <CardBody>
                  <div className="garage-card__head">
                    <div className="garage-card__avatar">
                      <i className="nc-icon nc-bank" />
                    </div>
                    <div className="garage-card__heading">
                      <h5 className="garage-card__title" title={branch.name}>
                        {branch.name}
                        {!branch.isActive && (
                          <span className="garage-card__closed">
                            {strings.deactivated}
                          </span>
                        )}
                      </h5>
                      <span className="garage-card__subtitle">
                        {[branch.city, branch.area].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                    {this.renderGarageMenu(branch, canWrite)}
                  </div>

                  <ul className="garage-card__meta">
                    {branch.address && (
                      <li>
                        <i className="nc-icon nc-square-pin" />
                        <span>{branch.address}</span>
                      </li>
                    )}
                    {branch.phoneNumber && (
                      <li>
                        <i className="nc-icon nc-mobile" />
                        <span>{branch.phoneNumber}</span>
                      </li>
                    )}
                    {branch.vehicleMakeList && (
                      <li>
                        <i className="nc-icon nc-delivery-fast" />
                        <span>{branch.vehicleMakeList}</span>
                      </li>
                    )}
                  </ul>

                  <div className="garage-card__actions">
                    {sections.map((section) => (
                      <button
                        type="button"
                        key={section.key}
                        className="garage-action"
                        onClick={() => this.handleOpenSection(branch, section)}
                      >
                        <i className={section.icon} />
                        {section.label}
                      </button>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  }

  renderTable(branches, canWrite) {
    const columns = [
      { Header: strings.name, accessor: "name" },
      { Header: strings.address, accessor: "address" },
      { Header: strings.city, accessor: "city" },
      { Header: strings.area, accessor: "area" },
      { Header: strings.vehicle_makes, accessor: "vehicleMakeList" },
      { Header: strings.phone, accessor: "phoneNumber" },
      {
        Header: strings.created,
        accessor: "created",
        Cell: (row) => row.value.toLocaleDateString(),
      },
      {
        Header: strings.actions,
        accessor: "id",
        sortable: false,
        filterable: false,
        minWidth: 220,
        Cell: (row) => this.renderTableActions(row.original, canWrite),
      },
    ];

    return (
      <Card>
        <CardBody>
          <ReactTable
            previousText={strings.previous}
            nextText={strings.next}
            loadingText={strings.loading}
            noDataText={strings.no_garages}
            pageText={strings.page}
            data={branches}
            defaultFilterMethod={this.filterCaseInsensitive}
            filterable
            loading={this.state.isLoading}
            columns={columns}
            defaultPageSize={10}
            showPaginationTop={false}
            showPaginationBottom
            PaginationComponent={TablePagination}
            className="-striped -highlight primary-pagination"
          />
        </CardBody>
      </Card>
    );
  }

  renderTableActions(branch, canWrite) {
    const sections = getGarageBrowseSections(branch.id);
    return (
      <RowActions
        actions={[
          ...sections.map((section) => ({
            key: section.key,
            icon: section.icon,
            label: section.label,
            onClick: () => this.handleOpenSection(branch, section),
          })),
          editAction(() => this.handleEdit(branch), canWrite),
          toggleActiveAction(
            branch.isActive,
            () => this.handleToggleActive(branch),
            canWrite
          ),
        ]}
      />
    );
  }

  renderEmpty() {
    const { selectedMerchant, showDeactivated } = this.state;
    const needsMerchant =
      !selectedMerchant || selectedMerchant === ":selectedMerchant";

    return (
      <Card>
        <CardBody>
          <div className="garages-empty">
            <i className="nc-icon nc-bank" />
            <p>
              {needsMerchant
                ? strings.select_merchant_first
                : showDeactivated
                ? strings.no_deactivated_garages
                : strings.no_garages}
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  render() {
    const {
      isLoading,
      merchants,
      selectedMerchant,
      search,
      view,
      showDeactivated,
      formOpen,
      editing,
    } = this.state;
    // a garage login only ever manages its own garage
    const isGarageAccount = Boolean(sessionStorage.getItem("garageBranchId"));
    const canWrite =
      this.hasPermission([Scopes.MERCHANTS_BRANCHES_WRITE]) && !isGarageAccount;
    const branches = this.getFilteredBranches();
    const showMerchantSelect =
      !sessionStorage.getItem("merchantId") &&
      !isGarageAccount &&
      merchants.length !== 1;

    return (
      <div className="content">
        <PageHeader
          icon="nc-icon nc-bank"
          title={showDeactivated ? strings.deactivated_garages : strings.garages}
          count={branches.length > 0 ? branches.length : undefined}
          subtitle={
            showDeactivated
              ? strings.deactivated_garages_subtitle
              : strings.garages_subtitle
          }
        >
          {showMerchantSelect && (
            <div className="garages-toolbar__merchant">
              <SelectRow
                hidelabel
                name="selectedMerchant"
                defaultValue={selectedMerchant}
                data={merchants}
                changeInput={this.handleMerchantSelect}
                placeholder={strings.merchant}
              />
            </div>
          )}

          <div className="garages-toolbar__search">
            <i className="nc-icon nc-zoom-split" />
            <input
              type="text"
              value={search}
              placeholder={strings.search_garages}
              onChange={(e) => this.setState({ search: e.target.value })}
            />
          </div>

          <div className="garages-view-toggle">
            <button
              type="button"
              className={view === "cards" ? "is-active" : ""}
              onClick={() => this.setState({ view: "cards" })}
            >
              <i className="nc-icon nc-bullet-list-67" />
              {strings.cards_view}
            </button>
            <button
              type="button"
              className={view === "table" ? "is-active" : ""}
              onClick={() => this.setState({ view: "table" })}
            >
              <i className="nc-icon nc-grid-45" />
              {strings.table_view}
            </button>
          </div>

          <Button
            color={showDeactivated ? "primary" : "default"}
            outline={!showDeactivated}
            onClick={this.handleToggleView}
          >
            <i
              className={
                showDeactivated ? "fa fa-arrow-left" : "fa fa-power-off"
              }
            />{" "}
            {showDeactivated
              ? strings.back_to_active_garages
              : strings.deactivated_garages}
          </Button>

          {canWrite && !showDeactivated && (
            <Button color="primary" onClick={this.handleAdd}>
              <i className="nc-icon nc-simple-add" /> {strings.add_garage}
            </Button>
          )}
        </PageHeader>

        {view === "table"
          ? this.renderTable(branches, canWrite)
          : isLoading
          ? this.renderProgress()
          : branches.length === 0
          ? this.renderEmpty()
          : this.renderCards(branches, canWrite)}

        <GarageFormModal
          isOpen={formOpen}
          garage={editing}
          merchantId={selectedMerchant}
          history={this.props.history}
          onClose={this.handleCloseForm}
          onSaved={this.handleSaved}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(MerchantBranchList);
